import _mapInstanceProperty from "@babel/runtime-corejs3/core-js/instance/map";
import _filterInstanceProperty from "@babel/runtime-corejs3/core-js/instance/filter";
import _Object$entries from "@babel/runtime-corejs3/core-js/object/entries";
import _JSON$stringify from "@babel/runtime-corejs3/core-js/json/stringify";
import _spliceInstanceProperty from "@babel/runtime-corejs3/core-js/instance/splice";
import React from "react";
import PropTypes from "prop-types";

const propsToAttributes = props => {
  var _context, _context2;

  return _mapInstanceProperty(_context = _filterInstanceProperty(_context2 = _Object$entries(props)).call(_context2, _ref => {
    let [, value] = _ref;
    return value;
  })).call(_context, _ref2 => {
    let [key, value] = _ref2;
    return `${key
    /* XSS */
    }=${_JSON$stringify(value)}`;
  }).join(' ');
};

const sanitizeProps = unsanitizedProps => {
  const {
    href,
    ...props
  } = unsanitizedProps;
  const sanitizedProps = { ...props
  };

  if (!sanitizedProps.imagesrcset && !sanitizedProps.imagesizes) {
    /**
     * If imagesrcset and imagesizes is given we don't set an href. That is because it causes browsers
     * with limited support for these attributes (like Safari) to load the `href` which will be wrong
     * in many cases because it doesnt take into account which image is the most appropriate to use
     * based on the device and conditions.
     */
    sanitizedProps.href = href;
  }

  return sanitizedProps;
};

export const createImagePreloadExtractor = () => {
  const ref = /*#__PURE__*/React.createRef();
  const links = [];
  ref.current = {
    links,
    addLink: (url, params) => {
      links.push([url, params]);
    },
    resetLinks: () => {
      _spliceInstanceProperty(links).call(links, 0);
    }
  };
  return {
    getLinkElements: () => _mapInstanceProperty(links).call(links, _ref3 => {
      let [, props] = _ref3;
      return /*#__PURE__*/React.createElement("link", sanitizeProps(props));
    }),
    getLinkTags: () => _mapInstanceProperty(links).call(links, _ref4 => {
      let [, props] = _ref4;
      return `<link ${propsToAttributes(sanitizeProps(props))} >`;
    }).join('\n'),
    ref
  };
};
const PreloadExtractorContext = /*#__PURE__*/React.createContext();
/**
 * Provider necessary to extract preloads from `Picture` and `Img` elements
 *
 * This component should only be used in the server-side or during static site generation
 *
 * Prefer this component when you rather not use `react-helmet`, `react-helmet-async` or `next/head` to extract link tags
 */

export const ImagePreloadExtractorManager = _ref5 => {
  let {
    extractor,
    children
  } = _ref5;
  return /*#__PURE__*/React.createElement(PreloadExtractorContext.Provider, {
    value: extractor.ref
  }, children);
};
const extractorType = process.env.NODE_ENV !== "production" ? PropTypes.shape({
  ref: PropTypes.shape({})
}) : {};
process.env.NODE_ENV !== "production" ? ImagePreloadExtractorManager.propTypes = {
  /**
   * Result of calling `createImagePreloadExtractor`
   */
  extractor: extractorType,
  children: PropTypes.node
} : void 0;
export const ImagePreloadLinkExtractor = _ref6 => {
  let {
    as,
    rel,
    media,
    href,
    imagesrcset,
    imagesizes
  } = _ref6;
  const preloadExtractor = React.useContext(PreloadExtractorContext);
  const isClient = React.useRef(false);

  if (preloadExtractor) {
    if (isClient.current) {
      // eslint-disable-next-line no-console
      console.warn('Preload extractor should only be provided during server side rendering');
    } else {
      preloadExtractor.current.addLink(href, {
        href,
        as,
        rel,
        media,
        imagesrcset,
        imagesizes
      });
    }
  }

  React.useEffect(() => {
    isClient.current = true;
  }, []);
  return null;
};
const PreloadRendererContext = /*#__PURE__*/React.createContext(props => /*#__PURE__*/React.createElement(ImagePreloadLinkExtractor, props));
/**
 * Provider used to override render strategy for preload tags. Prefer this component if you prefer to use `react-helmet`, `react-helmet-async` or `next/head` to extract link tags.
 */

export const ImagePreloadRenderer = _ref7 => {
  let {
    renderPreload,
    children
  } = _ref7;
  const sanitizedRender = React.useCallback(props => renderPreload(sanitizeProps(props)), [renderPreload]);
  return /*#__PURE__*/React.createElement(PreloadRendererContext.Provider, {
    value: sanitizedRender
  }, children);
};
process.env.NODE_ENV !== "production" ? ImagePreloadRenderer.propTypes = {
  /**
   * Override the default render strategy for image preloads
   */
  renderPreload: PropTypes.func,
  children: PropTypes.node
} : void 0;
export const useImagePreloadRenderer = givenRenderer => {
  const contextRenderer = React.useContext(PreloadRendererContext);
  return givenRenderer || contextRenderer;
};
/**
 * Preload a given image. Accepts other props accepted by `link[rel="preload"][as="image"]` elements
 */

export const ImagePreload = _ref8 => {
  let {
    href,
    ...props
  } = _ref8;
  const renderer = useImagePreloadRenderer();
  return (
    /*#__PURE__*/
    // Use JSX so that docs pick it up as a component
    React.createElement(React.Fragment, null, renderer({
      rel: 'preload',
      as: 'image',
      href,
      ...props
    }))
  );
};
ImagePreload.propTypes = {
  /**
   * href of image
   */
  href: PropTypes.string
};